'use client'

import React, { useEffect, useRef, useState } from 'react'
import { useLenis } from '@/providers/LenisProvider'

interface ScrollIndicatorProps {
  className?: string
  color?: string
  height?: string
  zIndex?: number
  useLenis?: boolean
}

export default function ScrollIndicator({
  className = '',
  color = '#F0FF2D', // Default accent color
  height = '4px',
  zIndex = 10000,
  useLenis: useLenisProp = true,
}: ScrollIndicatorProps) {
  const progressRef = useRef<HTMLDivElement>(null)
  const { lenis } = useLenis()
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (!progressRef.current) return

    const updateProgress = (scrollProgress: number) => {
      setProgress(scrollProgress)

      if (progressRef.current) {
        progressRef.current.style.width = `${scrollProgress * 100}%`
      }
    }

    const getNativeScrollProgress = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
      return scrollHeight > 0 ? scrollTop / scrollHeight : 0
    }

    const handleNativeScroll = () => {
      updateProgress(Math.min(Math.max(getNativeScrollProgress(), 0), 1))
    }

    if (useLenisProp && lenis) {
      const onLenisScroll = (e: { progress: number }) => {
        updateProgress(e.progress)
      }

      lenis.on('scroll', onLenisScroll)
      window.addEventListener('scroll', handleNativeScroll, { passive: true })
      handleNativeScroll()

      return () => {
        lenis.off('scroll', onLenisScroll)
        window.removeEventListener('scroll', handleNativeScroll)
      }
    }

    window.addEventListener('scroll', handleNativeScroll, { passive: true })
    handleNativeScroll()

    return () => {
      window.removeEventListener('scroll', handleNativeScroll)
    }
  }, [useLenisProp, lenis, color])

  return (
    <div
      className={`fixed top-0 left-0 w-full bg-gray-200/30 ${className}`}
      style={{
        height,
        zIndex,
      }}
    >
      <div
        ref={progressRef}
        className="h-full transition-all duration-100 ease-out"
        style={{
          backgroundColor: color,
          width: `${progress * 100}%`,
        }}
      />
    </div>
  )
}
